import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Help,
} from '@mui/icons-material';
import type { RecentTest } from '@/services/api/dashboardService';
import { canOpenTestResults, getTestOpeningBlockReason } from '@/lib/testStatus';

interface RecentTestsTableProps {
  tests: RecentTest[];
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return 'In progress';
  
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  const durationMs = end.getTime() - start.getTime();
  
  const minutes = Math.floor(durationMs / 60000);
  const seconds = Math.floor((durationMs % 60000) / 1000);
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Map backend test status to user-friendly display states.
 * Backend states: Created, BaselineStarted, BaselineCompleted, SpecimenStarted, 
 * SpecimenCompleted, Classifying, Complete, Failed, Cancelled, ClassificationFailed
 * 
 * Display states: Running, Failed, Sampled, Completed, Not Classified
 */
function mapTestStatus(status: string): { display: string; originalLabel: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' } {
  const statusLower = status.toLowerCase();

  // Map backend enum values to user-friendly display
  const mapping: Record<string, { display: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
    'created': { display: 'Running', color: 'info' },
    'baseline_started': { display: 'Running', color: 'info' },
    'baseline_completed': { display: 'Running', color: 'info' },
    'specimen_started': { display: 'Running', color: 'info' },
    'specimen_completed': { display: 'Running', color: 'info' },
    'classifying': { display: 'Running', color: 'info' },
    'complete': { display: 'Completed', color: 'success' },
    'failed': { display: 'Failed', color: 'error' },
    'cancelled': { display: 'Cancelled', color: 'error' },
    'classification_failed': { display: 'Not Classified', color: 'warning' },
    // Legacy/mock status names
    'running': { display: 'Running', color: 'info' },
    'sampled': { display: 'Completed', color: 'success' },
  };

  const mapped = mapping[statusLower] || { display: status, color: 'default' as const };
  
  // Convert snake_case to Title Case for tooltip
  const originalLabel = status
    .split(/(?=[A-Z])|_/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return {
    display: mapped.display,
    originalLabel,
    color: mapped.color,
  };
}

const getStatusIcon = (status?: string | null) => {
  switch (status) {
    case 'positive':
      return <CheckCircle fontSize="small" />;
    case 'negative':
      return <Cancel fontSize="small" />;
    case 'inconclusive':
      return <Help fontSize="small" />;
    case 'invalid':
      return <Cancel fontSize="small" />; // Use same icon as negative for invalid
    default:
      return null;
  }
};

const getStatusColor = (
  status?: string | null
): 'error' | 'success' | 'default' | 'primary' | 'warning' => {
  switch (status) {
    case 'positive':
      return 'error'; // red
    case 'negative':
      return 'success'; // green
    case 'inconclusive':
      return 'default'; // grey
    case 'invalid':
      return 'warning'; // orange/yellow for invalid
    default:
      return 'primary'; // blue (pending)
  }
};

interface TestStatusBadgeProps {
  status: string;
}

function TestStatusBadge({ status }: TestStatusBadgeProps) {
  const mapped = mapTestStatus(status);
  const tooltip = mapped.display === mapped.originalLabel 
    ? mapped.display 
    : `${mapped.display} (${mapped.originalLabel})`;

  return (
    <Chip
      label={mapped.display}
      color={mapped.color}
      size="small"
      title={tooltip}
      sx={{ fontWeight: 500 }}
    />
  );
}

export function RecentTestsTable({ tests }: RecentTestsTableProps) {
  const navigate = useNavigate();

  // Handle undefined or empty data
  if (!tests || !Array.isArray(tests) || tests.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No tests yet. Start your first test to see it here.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date/Time</TableCell>
            <TableCell>Profile Name</TableCell>
            <TableCell>Patient</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Pathogens</TableCell>
            <TableCell>Duration</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tests.map((test) => {
            const canOpen = canOpenTestResults(test.status);
            const blockReason = getTestOpeningBlockReason(test.status);
            
            return (
            <TableRow
              key={test.id}
              hover={canOpen}
              onClick={() => canOpen && navigate(`/measurement/results/${test.id}?from=${encodeURIComponent('/')}`)}
              sx={{ 
                cursor: canOpen ? 'pointer' : 'not-allowed',
                opacity: canOpen ? 1 : 0.6,
              }}
              title={!canOpen ? blockReason || '' : undefined}
            >
              <TableCell>{formatDateTime(test.startedAt)}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {test.profileName || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={400} color={test.patientName ? 'text.primary' : 'text.secondary'}>
                  {test.patientName || 'Anonymous'}
                </Typography>
              </TableCell>
              <TableCell>
                <TestStatusBadge status={test.status} />
              </TableCell>
              <TableCell>
                {test.pathogenClassificationResults &&
                test.pathogenClassificationResults.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {test.pathogenClassificationResults.map((result) => {
                      const statusIcon = getStatusIcon(result.status);
                      return (
                        <Chip
                          key={`${result.channelId}-${result.pathogenName || result.channelId}`}
                          label={result.pathogenName || `Channel ${result.channelId}`}
                          icon={statusIcon || undefined}
                          color={getStatusColor(result.status)}
                          size="small"
                          variant={result.status ? 'filled' : 'outlined'}
                          title={
                            result.status
                              ? `${result.status.toUpperCase()}: ${(result.confidence * 100).toFixed(1)}%`
                              : 'Pending classification'
                          }
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    -
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {formatDuration(test.startedAt, test.endedAt)}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
